<template>
  <div data-testid="linked-workflows-table" class="linked-workflows-table">
    <UITable
      id="linked-workflows"
      :columns="columns"
      :data="workflows"
      :row-key="rowKey"
      :row-props="rowProps"
      :enable-text-search="false"
      :enable-filter="false"
      :max-height="LINKED_WORKFLOWS_TABLE_MAX_HEIGHT"
      virtual-scroll
      row-class-name="linked-workflows-table__row"
      table-layout="fixed"
      transparent
      hide-footer
    />
    <p
      v-if="truncated"
      data-testid="linked-workflows-truncated"
      class="linked-workflows-table__note"
    >
      {{ t("common.workflowsShowingFirst", { count: workflows.length }) }}
    </p>
  </div>
</template>

<script lang="ts">
/**
 * Fixed row height. naive 2.38 has no min-row-height prop (and ghl-ui passes none): its
 * virtual list measures rendered rows, so every row must really be this tall.
 */
export const LINKED_WORKFLOWS_ROW_HEIGHT = 56;
/** Body height before the rows scroll under the sticky header: five rows */
export const LINKED_WORKFLOWS_TABLE_MAX_HEIGHT = LINKED_WORKFLOWS_ROW_HEIGHT * 5;
</script>

<script setup lang="ts">
import type { LinkedWorkflow } from "@/types/linkedWorkflows";
import { getStatusDisplay } from "@/util/linkedWorkflows";
import { LinkExternal01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UITable, UITag } from "@gohighlevel/ghl-ui";
import { computed, h } from "vue";
import { useI18n } from "vue-i18n";
import WorkflowActionsCell from "./WorkflowActionsCell.vue";
import WorkflowIconButton from "./WorkflowIconButton.vue";

defineProps<{
  workflows: LinkedWorkflow[];
  /** The server has more linked workflows than the single capped search returned */
  truncated: boolean;
}>();

const emit = defineEmits<{
  open: [workflow: LinkedWorkflow];
  "open-new-tab": [workflow: LinkedWorkflow];
  rename: [workflow: LinkedWorkflow];
  delete: [workflow: LinkedWorkflow];
}>();

const { t } = useI18n();

const rowKey = (row: LinkedWorkflow) => row.id;

// Only the visible rows are mounted (virtual-scroll), each at the fixed height
const rowStyle = { height: `${LINKED_WORKFLOWS_ROW_HEIGHT}px` };
const rowProps = (row: LinkedWorkflow) => ({
  style: rowStyle,
  onClick: () => emit("open", row),
});

const stopPropagation = (event: Event) => event.stopPropagation();

// Cells are rendered by the ghl-ui table, so they are styled with :deep() below
const renderName = (row: LinkedWorkflow) =>
  h("div", { class: "linked-workflows-table__name" }, [
    // A text button so keyboard users can open inline too; the row click stays for the mouse
    h(
      UIButton,
      {
        id: `wf-open-${row.id}`,
        text: true,
        class: "linked-workflows-table__name-button",
        title: row.name,
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          emit("open", row);
        },
        onMousedown: stopPropagation,
        onKeydown: stopPropagation,
      },
      { default: () => h("span", { class: "linked-workflows-table__name-text" }, row.name) }
    ),
    // Native title instead of a per-row tooltip component; the aria-label stays
    h(
      WorkflowIconButton,
      {
        id: `wf-open-new-tab-${row.id}`,
        class: "linked-workflows-table__open",
        label: newTabLabel.value,
        // New tab only -- nothing from this button may reach the row's inline open. The
        // native button already turns Enter/Space into this click, so keys need no handler.
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          emit("open-new-tab", row);
        },
        onMousedown: stopPropagation,
        onKeydown: stopPropagation,
      },
      { default: () => h(LinkExternal01Icon, { class: "linked-workflows-table__open-icon" }) }
    ),
  ]);

const newTabLabel = computed(() => t("common.openInNewTab"));

// Static t() calls keep the i18n keys statically analysable (no-dynamic-keys)
const statusLabels = computed<Record<string, string>>(() => ({
  "common.workflowStatusPublished": t("common.workflowStatusPublished"),
  "common.workflowStatusDraft": t("common.workflowStatusDraft"),
}));

const renderStatus = (row: LinkedWorkflow) => {
  const display = getStatusDisplay(row.status);
  const label = (display.labelKey && statusLabels.value[display.labelKey]) || display.label;
  return h(
    UITag,
    { type: display.tagType, size: "small", round: false, bordered: false },
    { default: () => label }
  );
};

const columns = computed(() => [
  {
    key: "name",
    title: t("common.workflowName"),
    width: "55%",
    render: renderName,
  },
  {
    key: "status",
    title: t("common.workflowStatus"),
    width: "15%",
    render: renderStatus,
  },
  {
    key: "updatedAt",
    title: t("common.workflowLastUpdated"),
    width: "20%",
    render: (row: LinkedWorkflow) =>
      h("span", { class: "linked-workflows-table__date" }, row.updatedAtLabel),
  },
  {
    key: "actions",
    title: "",
    width: "10%",
    render: (row: LinkedWorkflow) =>
      h(WorkflowActionsCell, {
        workflow: row,
        onRename: () => emit("rename", row),
        onDelete: () => emit("delete", row),
      }),
  },
]);
</script>

<style scoped>
.linked-workflows-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* One card: a single 1px border with 8px radius, no toolbar or extra padding (U7) */
.linked-workflows-table :deep(.table-container) {
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  box-shadow: none;
  background: var(--base-white, #fff);
}
.linked-workflows-table :deep(.ghl-table-custom-header) {
  display: none;
}
.linked-workflows-table :deep(.n-data-table),
.linked-workflows-table :deep(.n-data-table-wrapper) {
  border: 0;
  border-radius: 0;
}

/* Header: gray-50, dark 14px medium labels, vertical dividers (U5, G2) */
.linked-workflows-table :deep(.n-data-table-th) {
  height: 44px;
  padding: 0 16px;
  background-color: var(--gray-50, #f9fafb);
  border-bottom: 1px solid var(--gray-200, #eaecf0);
  color: var(--gray-900, #101828);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.linked-workflows-table :deep(.n-data-table-th:not(:last-child)) {
  border-right: 1px solid var(--gray-200, #eaecf0);
}

/* Rows: ~56px with 16px horizontal padding (U6) */
.linked-workflows-table :deep(.n-data-table-td) {
  height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
  color: var(--gray-900, #101828);
  font-size: 14px;
  line-height: 20px;
}
.linked-workflows-table :deep(.n-data-table-tr:last-child > .n-data-table-td) {
  border-bottom: 0;
}
.linked-workflows-table :deep(.linked-workflows-table__row) {
  cursor: pointer;
}
.linked-workflows-table :deep(.linked-workflows-table__row:hover > .n-data-table-td) {
  background-color: var(--gray-50, #f9fafb);
}

.linked-workflows-table :deep(.linked-workflows-table__name) {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}
/* Looks like plain row text; only the keyboard focus ring shows it is a button */
.linked-workflows-table :deep(.linked-workflows-table__name-button) {
  min-width: 0;
  max-width: 100%;
  justify-content: flex-start;
  font-size: 14px;
  line-height: 20px;
}
.linked-workflows-table :deep(.linked-workflows-table__name-button .n-button__content) {
  min-width: 0;
  overflow: hidden;
}
.linked-workflows-table :deep(.linked-workflows-table__name-button:focus-visible) {
  border-radius: 4px;
  outline: 2px solid var(--primary-600, #155eef);
  outline-offset: 2px;
}
.linked-workflows-table :deep(.linked-workflows-table__name-text) {
  display: block;
  overflow: hidden;
  color: var(--gray-900, #101828);
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The 36px button styling lives in WorkflowIconButton (shared with the kebab) */
.linked-workflows-table :deep(.linked-workflows-table__open-icon) {
  width: 18px;
  height: 18px;
}

.linked-workflows-table :deep(.linked-workflows-table__date) {
  color: var(--gray-600, #475467);
}

/* Status: gray rounded-rectangle pill (G1) */
.linked-workflows-table :deep(.n-tag) {
  border-radius: 6px;
}

.linked-workflows-table__note {
  margin: 0;
  color: var(--gray-500, #667085);
  font-size: 12px;
}
</style>
